import React, { useState, useEffect, useCallback } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { 
  ChevronLeftIcon, 
  ChevronRightIcon, 
  MagnifyingGlassMinusIcon, 
  MagnifyingGlassPlusIcon 
} from '@heroicons/react/24/outline';
import { apiService } from '../services/api';

// Настройка pdfjs worker для версии 6.x
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

interface PdfViewerProps {
  pdfPath: string | null;
  projectName: string | null;
  sessionId?: string;
  pdfFilename?: string;
}

const PdfViewer: React.FC<PdfViewerProps> = ({ pdfPath, projectName, sessionId, pdfFilename }) => {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(0.8);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pdfData, setPdfData] = useState<string | null>(null);

  const loadPdf = useCallback(async () => {
    if (!sessionId || !pdfFilename) return;

    try {
      setIsLoading(true);
      setError(null);
      
      // Загружаем PDF как blob с правильными заголовками авторизации
      const response = await fetch(apiService.getPdfUrl(sessionId, pdfFilename), {
        headers: {
          'Authorization': `Bearer ${apiService.getAuthToken()}`,
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      setPdfData(url);
    } catch (err) {
      setError('Ошибка загрузки PDF файла');
      console.error('Error loading PDF:', err);
    } finally {
      setIsLoading(false);
    }
  }, [sessionId, pdfFilename]);

  useEffect(() => {
    if (sessionId && pdfFilename) {
      loadPdf();
    } else {
      setPdfData(null);
      setError(null);
      setNumPages(null);
      setPageNumber(1);
    }
  }, [sessionId, pdfFilename, loadPdf]);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setPageNumber(1);
  };

  const onDocumentLoadError = (error: Error) => {
    setError('Ошибка загрузки PDF документа');
    console.error('PDF load error:', error);
  };

  const goToPreviousPage = () => {
    if (pageNumber > 1) {
      setPageNumber(pageNumber - 1);
    }
  };

  const goToNextPage = () => {
    if (numPages && pageNumber < numPages) {
      setPageNumber(pageNumber + 1);
    }
  };

  const goToPage = (page: number) => {
    if (page >= 1 && numPages && page <= numPages) {
      setPageNumber(page);
    }
  };

  const zoomIn = () => {
    setScale(prev => Math.min(prev + 0.25, 3.0));
  };

  const zoomOut = () => {
    setScale(prev => Math.max(prev - 0.25, 0.25));
  };

  const resetZoom = () => {
    setScale(0.8);
  };

  if (!sessionId || !pdfFilename) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 text-gray-400">
            <svg fill="none" stroke="currentColor" viewBox="0 0 48 48">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m0 0V9a2 2 0 012-2h2a2 2 0 012 2v3m0 0h6m-6 0v6m0-6H9m12 0v6m0-6h6" />
            </svg>
          </div>
          <h3 className="mt-2 text-sm font-medium text-gray-900">PDF не выбран</h3>
          <p className="mt-1 text-sm text-gray-500">Выберите проект для просмотра PDF</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Панель управления */}
      <div className="bg-gray-50 border-b border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <h3 className="text-lg font-medium text-gray-900">
              {projectName || 'PDF Документ'}
            </h3>
          </div>
          
          <div className="flex items-center space-x-4">
            {/* Навигация по страницам */}
            <div className="flex items-center space-x-2">
              <button
                onClick={goToPreviousPage}
                disabled={pageNumber <= 1}
                className="p-2 rounded-md border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
              >
                <ChevronLeftIcon className="h-4 w-4" />
              </button>
              
              <div className="flex items-center space-x-1">
                <input
                  type="number"
                  value={pageNumber}
                  onChange={(e) => goToPage(parseInt(e.target.value) || 1)}
                  className="w-16 px-2 py-1 text-sm border border-gray-300 rounded text-center"
                  min="1"
                  max={numPages || 1}
                />
                <span className="text-sm text-gray-600">
                  из {numPages || 0}
                </span>
              </div>

              <button
                onClick={goToNextPage}
                disabled={!numPages || pageNumber >= numPages}
                className="p-2 rounded-md border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
              >
                <ChevronRightIcon className="h-4 w-4" />
              </button>
            </div>

            {/* Масштабирование */}
            <div className="flex items-center space-x-2">
              <button
                onClick={zoomOut}
                className="p-2 rounded-md border border-gray-300 hover:bg-gray-100"
              >
                <MagnifyingGlassMinusIcon className="h-4 w-4" />
              </button>
              
              <button
                onClick={resetZoom}
                className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100"
              >
                {Math.round(scale * 100)}%
              </button>
              
              <button
                onClick={zoomIn}
                className="p-2 rounded-md border border-gray-300 hover:bg-gray-100"
              >
                <MagnifyingGlassPlusIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Область просмотра PDF */}
      <div className="flex-1 bg-gray-100 overflow-hidden">
        <div className="h-full w-full">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-2 text-gray-600">Загрузка PDF...</span>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="text-red-500 text-lg mb-2">⚠️</div>
                <p className="text-red-600">{error}</p>
                <button
                  onClick={loadPdf}
                  className="mt-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Попробовать снова
                </button>
              </div>
            </div>
          ) : pdfData ? (
            <div className="h-full w-full overflow-auto bg-white">
              <div className="p-4 min-w-max">
                <div className="bg-white shadow-lg inline-block">
                  <Document
                    file={pdfData}
                    onLoadSuccess={onDocumentLoadSuccess}
                    onLoadError={onDocumentLoadError}
                    loading={
                      <div className="flex items-center justify-center h-96 w-96">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        <span className="ml-2 text-gray-600">Обработка PDF...</span>
                      </div>
                    }
                    error={
                      <div className="flex items-center justify-center h-96 w-96">
                        <div className="text-center">
                          <div className="text-red-500 text-lg mb-2">⚠️</div>
                          <p className="text-red-600">Ошибка загрузки PDF</p>
                        </div>
                      </div>
                    }
                  >
                    <Page
                      pageNumber={pageNumber}
                      scale={scale}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                      loading={
                        <div className="flex items-center justify-center h-96 w-96">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                          <span className="ml-2 text-gray-600">Загрузка страницы...</span>
                        </div>
                      }
                      error={
                        <div className="flex items-center justify-center h-96 w-96">
                          <div className="text-center">
                            <div className="text-red-500 text-lg mb-2">⚠️</div>
                            <p className="text-red-600">Ошибка загрузки страницы</p>
                          </div>
                        </div>
                      }
                    />
                  </Document>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default PdfViewer; 